import { useState, useCallback } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { clearCart } from "@/store/features/cartSlice";
import {
  useCreateOrderMutation,
  useValidateCartMutation,
} from "@/store/api/orderApi";
import { useCreateAppointmentMutation } from "@/store/api/productApi";
import { CreateAppointmentPayload, CreateOrderPayload } from "@/types/order";
import { Branch } from "@/types/branch";
import { CartItem } from "@/types/cart";

interface Address {
  street?: string;
  city?: string;
  province?: string;
  country?: string;
  postal_code?: string;
}

interface CardData {
  name: string;
  cvv: string;
  month: string;
  year: string;
}

interface OrderItem {
  product_id: string | undefined;
  category_id: number | undefined;
  quantity: number;
  price_set: number;
  discount_set: number;
  branch_id: string | null;
  options: { name: string; quantity: number }[];
  selected_appointment_date_time?: Date;
  appointment_duration?: number;
  selected_service_provider?: { id: number; name: string };
}

interface UseOrderSubmissionProps {
  branch: Branch | undefined;
  branchId: string | null;
  merchantSlug: string;
  hasServiceInCart: boolean;
  adjustedCart: CartItem[];
  orderItems: OrderItem[];
  orderMode: string;
  state: string;
  token: string | null;
  cardData: CardData;
  address: Address;
  phone: string;
  comments: string;
  selectedPaymentMethod: string;
  subtotal: number;
  tax: number;
  tipAmount: number;
  total: number;
  serviceFee: number;
  deliveryFee: string;
  deliveryFeeCalculated: boolean;
  deliveryAddressId: number | undefined;
  usePoints: boolean;
  pointsToRedeem: number;
  discountCode: string;
  discountValue: number;
  promotionId: number | undefined;
  promotionName: string | undefined;
  validateForm: () => boolean;
  onDuplicatePhone: () => void;
}

export function useOrderSubmission({
  branch,
  branchId,
  merchantSlug,
  hasServiceInCart,
  adjustedCart,
  orderItems,
  orderMode,
  state,
  token,
  cardData,
  address,
  phone,
  comments,
  selectedPaymentMethod,
  subtotal,
  tax,
  tipAmount,
  total,
  serviceFee,
  deliveryFee,
  deliveryFeeCalculated,
  deliveryAddressId,
  usePoints,
  pointsToRedeem,
  discountCode,
  discountValue,
  promotionId,
  promotionName,
  validateForm,
  onDuplicatePhone,
}: UseOrderSubmissionProps) {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [orderSuccess, setOrderSuccess] = useState(false);

  const [orderCreate] = useCreateOrderMutation();
  const [createAppointment] = useCreateAppointmentMutation();
  const [validateCart] = useValidateCartMutation();

  const createAppointmentForService = useCallback(
    async (
      orderId: number | undefined,
      customerName: string,
      customerPhone: string,
    ) => {
      if (!orderId) return;

      const serviceProduct = orderItems[0];
      if (
        !serviceProduct?.selected_appointment_date_time ||
        !serviceProduct?.appointment_duration
      ) {
        toast.error("Please select an appointment time");
        return;
      }

      const requestBody: CreateAppointmentPayload = {
        branch: branchId ?? "",
        appointment_time: serviceProduct.selected_appointment_date_time,
        duration: serviceProduct.appointment_duration,
        employee: serviceProduct.selected_service_provider!.id,
        order: orderId,
        product: parseInt(serviceProduct.product_id!),
        status: "Scheduled",
        customer_name: customerName,
        phone_number: `+1${customerPhone}`,
      };

      try {
        await createAppointment(requestBody).unwrap();
      } catch (error: any) {
        console.error("Error creating appointment:", error);
        toast.error("Error creating appointment");
      }
    },
    [orderItems, branchId, createAppointment],
  );

  const submitOrder = useCallback(async () => {
    setLoading(true);
    const requestBody: CreateOrderPayload = {
      token: token ?? "",
      branch: branchId ?? "",
      state: state,
      order_items: adjustedCart.map((item) => ({
        product: item.id,
        quantity: item.quantity,
        price_set: parseFloat(item.unit_price ?? "0.00") * item.quantity,
        discount_set: 0,
        branch: branchId ?? "",
        options: Object.entries(item.selected_options)
          .filter(([_, option]) => option.selected)
          .map(([_, option]) => ({
            name: option.name,
            quantity: option.quantity,
          })),
      })),
      order_type: orderMode,
      sales_channel: "Online",
      payment_type: selectedPaymentMethod,
      card_data: {
        ...cardData,
        address: address?.street,
        city: address?.city,
        region: address?.province,
        country: address?.country,
        postal: address?.postal_code,
      },
      delivery_address:
        orderMode === "Delivery" ? deliveryAddressId : undefined,
      used_points: branch?.loyalty_program_enabled
        ? usePoints
          ? Math.ceil(pointsToRedeem)
          : 0
        : 0,
      points: branch?.loyalty_program_enabled
        ? Math.floor(
            subtotal *
              parseFloat(branch?.points_to_currency_multiplier || "1.00"),
          )
        : 0,
      total_price: parseFloat(total.toFixed(2)),
      subtotal_price: parseFloat(subtotal.toFixed(2)),
      tax: parseFloat(tax.toFixed(2)),
      tip: parseFloat(tipAmount.toFixed(2)),
      delivery_fees: parseFloat(
        parseFloat(deliveryFee.length > 0 ? deliveryFee : "0.00").toFixed(2),
      ),
      customer_name: cardData.name,
      phone_number: `+1${phone}`,
      comments,
      promotion_applied: discountCode ? true : false,
      promotion: discountCode ? promotionId : undefined,
      promotion_discount_value: discountValue,
      promotion_code: discountCode,
      promotion_name: discountCode ? promotionName : undefined,
    };

    if (orderMode === "Delivery" && !deliveryAddressId) {
      toast.error("Delivery address not validated. Please try again.");
      setLoading(false);
      return;
    }

    try {
      const res = await orderCreate(requestBody).unwrap();
      if (hasServiceInCart) {
        await createAppointmentForService(res.id, cardData.name, phone);
      }
      dispatch(clearCart());
      localStorage.removeItem("order_name");
      setOrderSuccess(true);
      const orderId = res?.order_name;
      navigate(`${merchantSlug}/order-confirmation?order_id=${orderId}`);
    } catch (error: any) {
      console.error("Order creation failed:", error.data?.error);
      if (
        error?.status === 400 &&
        error?.data?.error?.[0] ===
          `Customer with phone number +1${phone} already exists`
      ) {
        toast.error(error.data.error[0]);
        onDuplicatePhone();
      } else {
        toast.error(`Order creation failed due to : ${error.data?.error}`);
      }
    } finally {
      setLoading(false);
    }
  }, [
    token,
    branchId,
    state,
    adjustedCart,
    orderMode,
    selectedPaymentMethod,
    cardData,
    address,
    deliveryAddressId,
    branch,
    usePoints,
    pointsToRedeem,
    subtotal,
    total,
    tax,
    tipAmount,
    deliveryFee,
    phone,
    comments,
    discountCode,
    promotionId,
    discountValue,
    promotionName,
    hasServiceInCart,
    createAppointmentForService,
    orderCreate,
    dispatch,
    navigate,
    merchantSlug,
    onDuplicatePhone,
  ]);

  const submitPostPaidOrder = useCallback(
    async (customerName: string, customerPhone: string) => {
      setLoading(true);
      const requestBody: CreateOrderPayload = {
        token: token ?? "",
        branch: branchId ?? "",
        order_items: adjustedCart.map((item) => ({
          product: item.id,
          quantity: item.quantity,
          price_set: parseFloat(item.unit_price ?? "0.00") * item.quantity,
          discount_set: 0,
          branch: branchId ?? "",
          options: Object.entries(item.selected_options)
            .filter(([_, option]) => option.selected)
            .map(([_, option]) => ({
              name: option.name,
              quantity: option.quantity,
            })),
        })),
        order_type: "Appointment",
        sales_channel: "Online",
        payment_status: "PostPaid",
        total_price: parseFloat(total.toFixed(2)),
        subtotal_price: parseFloat(subtotal.toFixed(2)),
        tax: parseFloat(tax.toFixed(2)),
        customer_name: customerName,
        phone_number: `+1${customerPhone}`,
        comments: "PostPaid Order",
      };

      try {
        const res = await orderCreate(requestBody).unwrap();
        await createAppointmentForService(res.id, customerName, customerPhone);
        dispatch(clearCart());
        localStorage.removeItem("order_name");
        setOrderSuccess(true);
        const orderId = res?.order_name;
        navigate(`${merchantSlug}/order-confirmation?order_id=${orderId}`);
      } catch (error: any) {
        console.error("Order creation failed:", error.data?.error);
      } finally {
        setLoading(false);
      }
    },
    [
      token,
      branchId,
      adjustedCart,
      total,
      subtotal,
      tax,
      createAppointmentForService,
      orderCreate,
      dispatch,
      navigate,
      merchantSlug,
    ],
  );

  const handleCardPayment = useCallback(
    async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
      e.preventDefault();
      setError("");
      if (!token) {
        toast.error("Invalid Card Number");
        return;
      }
      if (!validateForm()) {
        setError("Please fix the errors in the form before submitting.");
        return;
      }
      if (!address.street) {
        toast.error("Please enter billing address to proceed");
        return;
      }
      if (!address.city) {
        toast.error("Please enter billing city to proceed");
        return;
      }
      if (!address.province) {
        toast.error("Please enter billing state to proceed");
        return;
      }
      if (!address.country) {
        toast.error("Please enter billing country to proceed");
        return;
      }
      if (!address.postal_code) {
        toast.error("Please enter billing postal code to proceed");
        return;
      }
      if (phone.trim() === "" || phone.replace(/\D/g, "").length !== 10) {
        setError("Please enter a valid 10-digit phone number");
        return;
      }
      await submitOrder();
    },
    [token, validateForm, address, phone, submitOrder],
  );

  const handleCheckoutClick = useCallback(
    async (onSuccess: () => void, onPickupOnly: () => void) => {
      setLoading(true);
      try {
        await validateCart({
          branch: branchId ?? "",
          order_type: hasServiceInCart ? "Appointment" : orderMode,
          sales_channel: "Online",
          order_items: adjustedCart.map((item) => ({
            product: item.id,
            quantity: item.quantity,
            price_set: parseFloat(item.unit_price ?? "0.00") * item.quantity,
            discount_set: 0,
            branch: branchId ?? "",
            options: Object.entries(item.selected_options)
              .filter(([_, option]) => option.selected)
              .map(([_, option]) => ({
                name: option.name,
                quantity: option.quantity,
              })),
          })),
          phone_number: `+1${phone}`,
          total_price: Number(total.toFixed(2)),
          subtotal_price: Number(subtotal.toFixed(2)),
        }).unwrap();

        if (!branch?.delivery_feature && !hasServiceInCart) {
          onPickupOnly();
        } else {
          onSuccess();
        }
      } catch (error: any) {
        console.error("Error:", error);
        toast.error(error.data?.error || "Error validating cart");
      } finally {
        setLoading(false);
      }
    },
    [
      branchId,
      hasServiceInCart,
      orderMode,
      adjustedCart,
      phone,
      total,
      subtotal,
      branch,
      validateCart,
    ],
  );

  return {
    loading,
    setLoading,
    error,
    setError,
    orderSuccess,
    submitOrder,
    submitPostPaidOrder,
    handleCardPayment,
    handleCheckoutClick,
  };
}
